import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "components/ui/button";
import { ButtonLink } from "components/ui/button-link";
import { Card, CardContent, CardTitle } from "components/ui/card";
import { PasswordInput } from "components/ui/password-input";
import { LoadingAnimation } from "components/ui/loading-animation";
import { Spinner } from "components/ui/spinner";
import { Icon } from "components/icons/Icon";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "components/ui/use-toast";
import { auth } from "services/api";
import { useAuth } from "contexts/AuthContext";
import { validatePasswordStrength } from "utils/passwordValidation";

export const ResetPassword: React.FC = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [tokenStatus, setTokenStatus] = useState<"checking" | "valid" | "invalid">("checking");
    const invalidToastShownRef = useRef(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { isAuthenticated, logout } = useAuth();

    useEffect(() => {
      if (isAuthenticated) {
        logout();
      }
    }, [isAuthenticated, logout]);

    const token = useMemo(() => searchParams.get("token")?.trim() ?? "", [searchParams]);
    const passwordValidation = useMemo(() => validatePasswordStrength(newPassword), [newPassword]);
    const passwordValidationMessage = passwordValidation.message;
    const passwordMismatch = newPassword.trim() !== "" && confirmPassword.trim() !== "" && newPassword !== confirmPassword;

    useEffect(() => {
      let cancelled = false;

      if (!token) {
        setTokenStatus("invalid");
        setFormError("This reset link is invalid or has expired. Please request a new one.");
        return;
      }

      setTokenStatus("checking");
      setFormError(null);
      auth.validateResetToken(token)
        .then(() => {
          if (cancelled) {
            return;
          }
          setTokenStatus("valid");
          setFormError(null);
          invalidToastShownRef.current = false;
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }
          const apiError = (error as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
          const message = apiError?.detail || apiError?.error || "This reset link is invalid or has expired. Please request a new one.";
          setTokenStatus("invalid");
          setFormError(message);
          if (!invalidToastShownRef.current) {
            addToast({
              variant: "destructive",
              title: "Invalid reset link",
              description: message,
            });
            invalidToastShownRef.current = true;
          }
        });

      return () => {
        cancelled = true;
      };
    }, [token, addToast]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setFormError(null);

        if (tokenStatus !== "valid") {
          return;
        }

        if (passwordValidationMessage) {
          setFormError(passwordValidationMessage);
          return;
        }

        if (passwordMismatch) {
          setFormError("Passwords do not match. Please try again.");
          return;
        }

        try {
          setIsLoading(true);
          await auth.resetPassword({ token, newPassword });
          addToast({
            title: "Password updated",
            description: "You can now sign in with your new password.",
          });
          navigate("/login", { replace: true });
        } catch (error: unknown) {
          const apiError = (error as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
          const message = apiError?.detail || apiError?.error || "We couldn't reset your password. Please request a new link and try again.";
          setFormError(message);
          if (message.toLowerCase().includes("invalid") || message.toLowerCase().includes("expired") || message.toLowerCase().includes("already")) {
            setTokenStatus("invalid");
          }
          addToast({
            variant: "destructive",
            title: "Reset failed",
            description: message,
          });
        } finally {
          setIsLoading(false);
        }
      };

  const isSubmitDisabled =
    tokenStatus !== "valid" ||
    newPassword.trim() === '' ||
    confirmPassword.trim() === '' ||
    passwordMismatch ||
    Boolean(passwordValidationMessage);

  const showForm = tokenStatus === "valid";
  const showCheckingState = tokenStatus === "checking";
  return (
    <main
      className="min-h-screen w-full flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12"
      data-model-id="58:7900"
    >
      <Card maxWidth="narrow">
        <div className="flex justify-center">
          <Icon name="logo" size={64} />
        </div>
        <CardTitle
          centered={true}
          title={
            <p className="leading-tight">
              Reset Password
            </p>
          }
        />
        
        <CardContent variant="form" className="pt-4">
          {formError && (
            <div className="w-full rounded-md text-sm text-red-600 text-center" role="alert">
              {formError}
            </div>
          )}

          {showCheckingState && (
            <div className="flex justify-center py-6">
              <LoadingAnimation size="sm" />
            </div>
          )}

          {!showCheckingState && !showForm && (
            <div className="flex flex-col items-center gap-3 py-6 text-center text-sm text-muted-foreground">
              <ButtonLink className="text-[14px]" to="/forgot-password">
                Request a new password reset
              </ButtonLink>
              <ButtonLink className="text-[14px]" to="/login">
                Log In
              </ButtonLink>
            </div>
          )}

          {showForm && (
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <PasswordInput
                size="sm"
                placeholder="New password"
                value={newPassword}
                autoComplete="new-password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
              {passwordValidationMessage && (
                <p className="mt-1 text-xs font-medium text-destructive">{passwordValidationMessage}</p>
              )}
              <PasswordInput
                size="sm"
                placeholder="Confirm password"
                value={confirmPassword}
                autoComplete="new-password"
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {passwordMismatch && (
                <p className="text-sm text-red-600 text-center">Passwords must match.</p>
              )}

              <Button type="submit" variant="default" disabled={isSubmitDisabled || isLoading}>
                {isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    "Submit"
                  )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
};
